import api from "@services/api";
import { saveGoogleApiUsageLog } from "@hooks/useGoogleApiUsageLog";
import { GoogleApiRequestTypes } from "@shared/constants/google-api-request-types.enum";
import { AddressByCoordinates } from "@shared/interfaces/address-by-coordinates.interface";

/** User fields sent with the Google API usage log */
interface AddressLookupUser {
  name?: string;
  email?: string;
}

type Coordinates = google.maps.LatLng | google.maps.LatLngLiteral;

const toLiteral = (location: Coordinates): google.maps.LatLngLiteral => (
  typeof location.lat === "function"
    ? { lat: (location as google.maps.LatLng).lat(), lng: (location as google.maps.LatLng).lng() }
    : location as google.maps.LatLngLiteral
);

/**
 * The platform's own geocoder (Nominatim, through the Backend)
 * @returns The address, or null when it has no answer or cannot be reached
 */
const readOwnAddress = async ({ lat, lng }: google.maps.LatLngLiteral): Promise<google.maps.GeocoderResult | null> => {
  try {
    const { data } = await api.get("/locations/address/read", { params: { latitude: lat, longitude: lng } });
    const result: AddressByCoordinates | null = data?.status === "success" ? data.result : null;

    // Same shape as a Google Geocoder result: the screens read formatted_address and address_components
    return result?.formatted_address ? result as unknown as google.maps.GeocoderResult : null;
  } catch (error) {
    return null;
  }
};

/**
 * The Google Geocoder, counted in the Google API usage log
 * @returns The address, or null when Google has no answer
 */
const readGoogleAddress = (location: google.maps.LatLngLiteral, user?: AddressLookupUser): Promise<google.maps.GeocoderResult | null> => {
  saveGoogleApiUsageLog(GoogleApiRequestTypes.FIXED_POINT_MAP, user);

  return new Promise((resolve) => {
    new google.maps.Geocoder().geocode({ location }, (results, status) => {
      resolve(status === google.maps.GeocoderStatus.OK && results?.[0] ? results[0] : null);
    });
  });
};

/**
 * Address of a point shown or clicked on a map: the platform's own geocoder first, the Google Geocoder only when it
 * has no answer (and only that call goes to the Google API usage log)
 * @param location Coordinates
 * @param user Logged user, for the Google API usage log
 * @returns The address as a Google Geocoder result, or null when neither geocoder knows the place
 */
export const getAddressByCoordinates = async (location: Coordinates, user?: AddressLookupUser): Promise<google.maps.GeocoderResult | null> => {
  const literal = toLiteral(location);

  return (await readOwnAddress(literal)) ?? readGoogleAddress(literal, user);
};
